import { CheckCircle2, Circle, LoaderCircle, XCircle } from 'lucide-react';

import type { ActionReceipt } from '../model/types';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Button } from '@/shared/ui/shadcn/button';
import aiGlassSparkle from '@/shared/assets/animations/ai-glass-sparkle.gif';

export interface RequestsCompletionDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  onGoToNextPatient(): void;
  onStay(): void;
  patientName: string;
  nextPatientName: string | null | undefined;
  actions: readonly ActionReceipt[];
  isSuggesting?: boolean;
}

export function RequestsCompletionDialog({
  open,
  onOpenChange,
  onGoToNextPatient,
  onStay,
  patientName,
  nextPatientName,
  actions,
  isSuggesting = false,
}: RequestsCompletionDialogProps) {
  const descriptionId = 'requests-completion-dialog-description';
  const titleId = 'requests-completion-dialog-title';

  const handleStay = () => {
    onStay();
    onOpenChange(false);
  };

  const handleGoToNextPatient = () => {
    if (nextPatientName === null || isSuggesting) {
      return;
    }
    onGoToNextPatient();
  };

  const description =
    nextPatientName === null
      ? "You're all caught up! No other patients need attention right now."
      : nextPatientName === undefined
        ? 'Ready to help another patient next?'
        : `Ready to help ${nextPatientName} next?`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={descriptionId}
        aria-labelledby={titleId}
        overlayClassName="bg-black/40 backdrop-blur-sm"
        className="rounded-xl bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/70"
      >
        <DialogHeader className="flex flex-col items-center text-center">
          <img
            src={aiGlassSparkle}
            alt="Celebration sparkle"
            className="h-40 w-40 object-contain"
          />
          <DialogTitle id={titleId}>Great work! {patientName} is all set</DialogTitle>
          <DialogDescription id={descriptionId}>{description}</DialogDescription>
        </DialogHeader>

        <section className="mx-auto w-full max-w-md space-y-2" aria-labelledby="action-status">
          <h3 id="action-status" className="text-sm font-semibold text-foreground">
            Action status:
          </h3>
          {actions.length > 0 ? (
            <ul className="max-h-40 space-y-2 overflow-y-auto">
              {actions.map((action) => {
                const status =
                  action.status === 'Succeeded'
                    ? {
                        label: 'Success',
                        className: 'text-emerald-500',
                        icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
                      }
                    : action.status === 'Failed'
                      ? {
                          label: 'Failed',
                          className: 'text-destructive',
                          icon: <XCircle className="size-4" aria-hidden="true" />,
                        }
                      : action.status === 'Running'
                        ? {
                            label: 'In progress',
                            className: 'text-primary',
                            icon: (
                              <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
                            ),
                          }
                        : {
                            label: 'Not run',
                            className: 'text-muted-foreground',
                            icon: <Circle className="size-4" aria-hidden="true" />,
                          };
                return (
                  <li
                    key={action.externalId}
                    className="flex items-center gap-3 rounded-md bg-muted/30 px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 flex-1 text-foreground">{action.title}</span>
                    <span className={`flex shrink-0 items-center gap-1 ${status.className}`}>
                      {status.icon}
                      {status.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No actions.</p>
          )}
        </section>

        <DialogFooter className="flex justify-center sm:justify-center">
          <Button type="button" variant="secondary" onClick={handleStay}>
            Stay here
          </Button>
          <Button
            type="button"
            onClick={handleGoToNextPatient}
            disabled={nextPatientName === null || isSuggesting}
          >
            {isSuggesting ? 'Finding…' : 'Keep the momentum going'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
